import React, { useMemo, useState } from 'react';

import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from 'components/IAP/hooks/incidentInfoHooks';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import IapAttachmentSelectionList from 'components/IAP/IapAttachmentSelectionList';
import {
  areManifestsEqual,
  resolveIapAttachmentManifest,
  type IapAttachmentManifest,
} from 'components/IAP/iapAttachmentManifest';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type IapBundleConfirmDialogProps = {
  show: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  isGenerating?: boolean;
};

/**
 * Confirms what the IAP will contain before it is built.
 *
 * Changes made here are saved before generating, because the bundle is
 * assembled server side from the stored manifest.
 */
const IapBundleConfirmDialog: React.FC<IapBundleConfirmDialogProps> = ({
  show,
  onClose,
  onConfirm,
  isGenerating = false,
}) => {
  const { data: incidentInfo } = useIncidentInfo();
  const incidentMutation = useUpdateIcsInfo();
  const family = useIcsFormFamily();

  const savedManifest = useMemo(
    () =>
      resolveIapAttachmentManifest(incidentInfo?.iapAttachmentManifest, family),
    [incidentInfo?.iapAttachmentManifest, family]
  );

  const [draft, setDraft] = useState<IapAttachmentManifest | null>(null);
  const manifest = draft ?? savedManifest;
  const isDirty = draft !== null && !areManifestsEqual(draft, savedManifest);

  // Dropped on the way out rather than on the way in, so reopening shows what is
  // stored instead of a stale edit.
  const handleClose = () => {
    setDraft(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (isDirty && draft) {
      await incidentMutation.mutateAsync({
        ...incidentInfo,
        iapAttachmentManifest: draft,
      });
    }
    setDraft(null);
    await onConfirm();
  };

  const isBusy = isGenerating || incidentMutation.isPending;

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open && !isBusy) handleClose();
      }}
    >
      <DialogContent className="dark tw:max-w-2xl tw:text-foreground">
        <DialogHeader>
          <DialogTitle>Generate Incident Action Plan</DialogTitle>
          <DialogDescription>
            These attachments will be listed on the cover and merged into the
            plan. Anything left unchecked is excluded from the exported
            document.
          </DialogDescription>
        </DialogHeader>

        <IapAttachmentSelectionList
          manifest={manifest}
          family={family}
          disabled={isBusy}
          onChange={setDraft}
        />

        {manifest.otherAttachments.length > 0 && (
          <div className="tw:flex tw:flex-col tw:gap-1">
            <div className="tw:text-sm tw:font-medium tw:text-foreground">
              Also named on the cover
            </div>
            <ul className="tw:m-0 tw:list-disc tw:pl-5 tw:text-sm tw:text-foreground">
              {manifest.otherAttachments.map((label, index) => (
                <li key={index}>{label}</li>
              ))}
            </ul>
          </div>
        )}

        {manifest.forms.length === 0 && (
          <p className="tw:text-sm tw:text-destructive">
            Select at least one form to include in the IAP.
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isBusy}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isBusy || manifest.forms.length === 0}
            onClick={() => void handleConfirm()}
          >
            {isBusy ? 'Generating...' : 'Generate IAP'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IapBundleConfirmDialog;
